<template>
    <div class="dataview-page">
        <!-- 工具条：说明 + 操作 -->
        <div class="dv-toolbar">
            <div class="dv-toolbar__info">
                <span class="dv-toolbar__icon">
                    <el-icon>
                        <DataAnalysis />
                    </el-icon>
                </span>
                <span class="dv-toolbar__meta">
                    <span class="dv-toolbar__title">数据视图</span>
                    <span class="dv-toolbar__subtitle">配置数据源与展示区块，编排对外可见的数据看板；拖动左侧手柄可调整顺序</span>
                </span>
            </div>
            <div class="dv-toolbar__actions">
                <el-button class="dv-btn dv-btn--ghost" :loading="listLoading" @click="loadList">
                    <el-icon v-if="!listLoading" class="dv-btn__icon">
                        <Refresh />
                    </el-icon>
                    刷新
                </el-button>
                <el-button class="dv-btn" type="primary" @click="openCreate">
                    <el-icon class="dv-btn__icon">
                        <Plus />
                    </el-icon>
                    新建视图
                </el-button>
            </div>
        </div>

        <!-- 视图列表 -->
        <section class="dv-panel" v-loading="listLoading">
            <el-empty v-if="dataViewList.length === 0 && !listLoading" description="还没有数据视图，点击右上角「新建视图」开始" />
            <el-table v-else ref="dataViewTable" :data="dataViewList" row-key="id" class="dv-table" stripe style="width: 100%">
                <el-table-column width="46" align="center">
                    <template #default>
                        <span class="drag-handle table-drag-handle"><i></i><i></i><i></i></span>
                    </template>
                </el-table-column>
                <el-table-column label="视图名称" min-width="180" show-overflow-tooltip>
                    <template #default="scope">
                        <span class="dv-name">{{ scope.row.name }}</span>
                    </template>
                </el-table-column>
                <el-table-column label="描述信息" min-width="240" show-overflow-tooltip>
                    <template #default="scope">
                        <span class="dv-desc">{{ scope.row.description || '暂无描述' }}</span>
                    </template>
                </el-table-column>
                <el-table-column label="状态" width="130" align="center">
                    <template #default="scope">
                        <div class="dv-status">
                            <el-switch v-model="scope.row.isEnable" class="dv-switch"
                                :loading="!!scope.row._enableLoading" @change="enableRow(scope.row)" />
                            <span class="dv-status__text" :class="scope.row.isEnable ? 'is-on' : 'is-off'">
                                {{ scope.row.isEnable ? '启用' : '停用' }}
                            </span>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column prop="createTime" label="创建时间" width="170">
                    <template #default="scope">
                        <span class="dv-time">{{ scope.row.createTime || '—' }}</span>
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="150" align="center" fixed="right">
                    <template #default="scope">
                        <el-link class="dv-op" type="primary" @click="openEdit(scope.row)">编辑</el-link>
                        <el-popconfirm width="230" title="删除操作不可撤回，确定删除该视图吗？" confirm-button-text="确定删除"
                            cancel-button-text="取消" @confirm="remove(scope.row)">
                            <template #reference>
                                <el-link class="dv-op" type="danger">删除</el-link>
                            </template>
                        </el-popconfirm>
                    </template>
                </el-table-column>
            </el-table>
        </section>
    </div>

    <!-- Form -->
    <el-dialog class="dv-dialog" :title="dialogFormMode == 'create' ? '创建数据视图' : '编辑数据视图'" v-model="dialogFormVisible"
        width="min(1100px, 94vw)" top="6vh" :close-on-click-modal="false">
        <el-tabs v-model="activeTab">
            <el-tab-pane name="base">
                <template #label>
                    <span class="dv-tab-label">基本信息</span>
                </template>
                <div class="dv-info">
                    <div class="dv-info__title">基本信息</div>
                    <div class="dv-info__body">
                        <el-form :model="dataForm" label-width="100px">
                            <el-form-item label="视图名称">
                                <el-input v-model="dataForm.name" maxlength="50" show-word-limit autocomplete="off"
                                    placeholder="请输入视图名称"></el-input>
                            </el-form-item>
                            <el-form-item label="描述信息">
                                <el-input type="textarea" :rows="2" maxlength="200" show-word-limit
                                    placeholder="简要说明该视图的用途" v-model="dataForm.description">
                                </el-input>
                            </el-form-item>
                        </el-form>
                    </div>
                </div>

                <div class="dv-info">
                    <div class="dv-info__title">样式</div>
                    <div class="dv-info__body">
                        <el-form :model="dataForm.style" label-width="100px">
                            <el-form-item label="头像">
                                <el-input v-model="dataForm.style.avatar" placeholder="头像地址"></el-input>
                            </el-form-item>
                            <el-form-item label="背景">
                                <el-input v-model="dataForm.style.background" placeholder="背景图地址"></el-input>
                            </el-form-item>
                        </el-form>
                    </div>
                </div>

                <div class="dv-info">
                    <div class="dv-info__title">团队</div>
                    <div class="dv-info__body">
                        <el-select v-model="dataForm.teamIdList" multiple filterable placeholder="选择团队" style="width:100%">
                            <el-option v-for="team in ownerTeams" :key="team.id" :label="team.name"
                                :value="team.id"></el-option>
                        </el-select>
                        <p class="dv-info__tip">不选择团队时，该视图仅自己可见</p>
                    </div>
                </div>
            </el-tab-pane>

            <el-tab-pane name="sources">
                <template #label>
                    <span class="dv-tab-label">数据源<em class="dv-tab-count">{{ sourceCount }}</em></span>
                </template>
                <div class="dv-info">
                    <div class="dv-info__title">数据库源</div>
                    <div class="dv-info__body">
                        <div v-for="(item, index) in dataForm.srcDbs" :key="index" class="dv-src-item">
                            <div class="dv-src-item__head">
                                <span class="dv-src-item__badge">数据库 {{ index + 1 }}</span>
                                <el-button link type="danger" @click="removeItem(dataForm.srcDbs, index)">删除</el-button>
                            </div>
                            <div class="dv-fields">
                                <div class="dv-field">
                                    <span class="dv-field__label">名称</span>
                                    <el-input v-model="item.name" placeholder="请输入名称" />
                                </div>
                                <div class="dv-field">
                                    <span class="dv-field__label">URL</span>
                                    <el-input v-model="item.url" placeholder="jdbc:mysql://host:3306/db" />
                                </div>
                                <div class="dv-field">
                                    <span class="dv-field__label">类型</span>
                                    <el-select v-model="item.type" placeholder="请选择类型" clearable style="width:100%">
                                        <el-option label="MySQL" value="mysql"></el-option>
                                        <el-option label="PostgreSQL" value="postgresql"></el-option>
                                    </el-select>
                                </div>
                                <div class="dv-field">
                                    <span class="dv-field__label">驱动类</span>
                                    <el-input v-model="item.driverClass" placeholder="驱动类" />
                                </div>
                                <div class="dv-field">
                                    <span class="dv-field__label">用户名</span>
                                    <el-input v-model="item.userName" autocomplete="off" placeholder="用户名" />
                                </div>
                                <div class="dv-field">
                                    <span class="dv-field__label">密码</span>
                                    <el-input v-model="item.password" type="password" show-password
                                        autocomplete="new-password" placeholder="密码" />
                                </div>
                            </div>
                        </div>
                        <p v-if="dataForm.srcDbs.length === 0" class="dv-empty-tip">暂无数据库源</p>
                        <button type="button" class="dv-add-btn" @click="addSrcDb">
                            <el-icon>
                                <Plus />
                            </el-icon>
                            添加数据库源
                        </button>
                    </div>
                </div>

                <div class="dv-info">
                    <div class="dv-info__title">SSH源</div>
                    <div class="dv-info__body">
                        <div v-for="(item, index) in dataForm.srcSshs" :key="index" class="dv-src-item">
                            <div class="dv-src-item__head">
                                <span class="dv-src-item__badge">SSH {{ index + 1 }}</span>
                                <el-button link type="danger" @click="removeItem(dataForm.srcSshs, index)">删除</el-button>
                            </div>
                            <div class="dv-fields">
                                <div class="dv-field">
                                    <span class="dv-field__label">名称</span>
                                    <el-input v-model="item.name" placeholder="请输入名称" />
                                </div>
                                <div class="dv-field">
                                    <span class="dv-field__label">IP</span>
                                    <el-input v-model="item.ip" placeholder="127.0.0.1" />
                                </div>
                                <div class="dv-field">
                                    <span class="dv-field__label">端口</span>
                                    <el-input v-model="item.port" placeholder="22" />
                                </div>
                                <div class="dv-field">
                                    <span class="dv-field__label">用户名</span>
                                    <el-input v-model="item.userName" autocomplete="off" placeholder="用户名" />
                                </div>
                                <div class="dv-field">
                                    <span class="dv-field__label">密码</span>
                                    <el-input v-model="item.password" type="password" show-password
                                        autocomplete="new-password" placeholder="密码" />
                                </div>
                            </div>
                        </div>
                        <p v-if="dataForm.srcSshs.length === 0" class="dv-empty-tip">暂无SSH源</p>
                        <button type="button" class="dv-add-btn" @click="addSrcSsh">
                            <el-icon>
                                <Plus />
                            </el-icon>
                            添加SSH源
                        </button>
                    </div>
                </div>

                <div class="dv-info">
                    <div class="dv-info__title">URL源</div>
                    <div class="dv-info__body">
                        <div v-for="(item, index) in dataForm.srcUrls" :key="index" class="dv-src-item">
                            <div class="dv-src-item__head">
                                <span class="dv-src-item__badge">URL {{ index + 1 }}</span>
                                <el-button link type="danger" @click="removeItem(dataForm.srcUrls, index)">删除</el-button>
                            </div>
                            <div class="dv-fields">
                                <div class="dv-field">
                                    <span class="dv-field__label">名称</span>
                                    <el-input v-model="item.name" placeholder="请输入名称" />
                                </div>
                                <div class="dv-field">
                                    <span class="dv-field__label">URL</span>
                                    <el-input v-model="item.url" placeholder="https://example.com/api" />
                                </div>
                                <div class="dv-field">
                                    <span class="dv-field__label">请求方式</span>
                                    <el-select v-model="item.type" placeholder="请选择" style="width:100%">
                                        <el-option label="GET" value="GET"></el-option>
                                        <el-option label="POST" value="POST"></el-option>
                                    </el-select>
                                </div>
                            </div>
                        </div>
                        <p v-if="dataForm.srcUrls.length === 0" class="dv-empty-tip">暂无URL源</p>
                        <button type="button" class="dv-add-btn" @click="addSrcUrl">
                            <el-icon>
                                <Plus />
                            </el-icon>
                            添加URL源
                        </button>
                    </div>
                </div>

                <div class="dv-info">
                    <div class="dv-info__title">ES源</div>
                    <div class="dv-info__body">
                        <div v-for="(item, index) in dataForm.srcEs" :key="index" class="dv-src-item">
                            <div class="dv-src-item__head">
                                <span class="dv-src-item__badge">ES {{ index + 1 }}</span>
                                <el-button link type="danger" @click="removeItem(dataForm.srcEs, index)">删除</el-button>
                            </div>
                            <div class="dv-fields">
                                <div class="dv-field">
                                    <span class="dv-field__label">名称</span>
                                    <el-input v-model="item.name" placeholder="请输入名称" />
                                </div>
                                <div class="dv-field">
                                    <span class="dv-field__label">ServerUrl</span>
                                    <el-input v-model="item.serverUrl" placeholder="http://host:9200" />
                                </div>
                                <div class="dv-field">
                                    <span class="dv-field__label">用户名</span>
                                    <el-input v-model="item.username" autocomplete="off" placeholder="用户名" />
                                </div>
                                <div class="dv-field">
                                    <span class="dv-field__label">密码</span>
                                    <el-input v-model="item.password" type="password" show-password
                                        autocomplete="new-password" placeholder="密码" />
                                </div>
                            </div>
                        </div>
                        <p v-if="dataForm.srcEs.length === 0" class="dv-empty-tip">暂无ES源</p>
                        <button type="button" class="dv-add-btn" @click="addSrcEs">
                            <el-icon>
                                <Plus />
                            </el-icon>
                            添加ES源
                        </button>
                    </div>
                </div>
            </el-tab-pane>

            <el-tab-pane name="sections">
                <template #label>
                    <span class="dv-tab-label">区块配置<em class="dv-tab-count">{{ dataForm.sections.length }}</em></span>
                </template>
                <div ref="sectionList" class="section-list">
                    <div v-for="section in dataForm.sections" :key="section._id" class="section-box"
                        :data-section-id="section._id" :style="sectionBoxStyle(section)">
                        <div class="section-box-header">
                            <span class="drag-handle section-drag-handle"><i></i><i></i><i></i></span>
                            <el-input v-model="section.name" placeholder="区块名称" size="small"
                                class="section-name-input" />
                            <span class="dv-count-pill">{{ section.cards.length }} 张卡片</span>
                            <el-button size="small" type="danger" plain @click.stop="removeSection(section)">删除区块</el-button>
                        </div>
                        <div class="card-list">
                            <div v-for="card in section.cards" :key="card._id" class="card-box" :data-card-id="card._id">
                                <div class="card-box-header">
                                    <span class="drag-handle card-drag-handle"><i></i><i></i><i></i></span>
                                    <el-input v-model="card.name" placeholder="卡片名称" size="small"
                                        class="card-name-input" />
                                    <el-select v-model="card.type" placeholder="卡片类型" clearable size="small"
                                        class="card-type-select">
                                        <el-option v-for="t in cardTypeOptions" :key="t.value" :label="t.label"
                                            :value="t.value">
                                            <el-tooltip :content="t.desc" placement="right">
                                                <span>{{ t.label }}</span>
                                            </el-tooltip>
                                        </el-option>
                                    </el-select>
                                    <el-cascader v-model="card._src" :options="srcCascaderOptions"
                                        placeholder="数据源" clearable size="small"
                                        class="card-src-select" @change="v => onCascaderChange(card, v)" />
                                    <el-button link type="danger" @click.stop="removeCard(card)">删除</el-button>
                                </div>
                                <div class="card-box-body">
                                    <el-input v-model="card.paramsText" type="textarea" :rows="5"
                                        placeholder="参数(JSON格式)，例如：{ &quot;limit&quot;: 10 }" size="small"
                                        class="card-params-input" />
                                </div>
                            </div>
                            <p v-if="section.cards.length === 0" class="dv-empty-tip dv-empty-tip--center">该区块还没有卡片</p>
                        </div>
                        <div class="add-card-btn-wrap">
                            <button type="button" class="dv-add-btn dv-add-btn--block" @click="addCard(section)">
                                <el-icon>
                                    <Plus />
                                </el-icon>
                                添加卡片
                            </button>
                        </div>
                    </div>
                    <el-empty v-if="dataForm.sections.length === 0" description="还没有区块，点击下方按钮添加" :image-size="70" />
                </div>
                <div class="add-section-btn-wrap">
                    <button type="button" class="dv-add-btn dv-add-btn--block dv-add-btn--lg" @click="addSection">
                        <el-icon>
                            <Plus />
                        </el-icon>
                        添加区块
                    </button>
                </div>
            </el-tab-pane>
        </el-tabs>

        <template #footer>
            <span class="dialog-footer">
                <el-button @click="dialogFormVisible = false">取 消</el-button>
                <el-button type="primary" :loading="saving" @click="save">保 存</el-button>
            </span>
        </template>
    </el-dialog>
</template>

<script>
import { ElMessage } from 'element-plus';
import Sortable from 'sortablejs';
import { Plus, Refresh, DataAnalysis } from '@element-plus/icons-vue';
import request from '../../../utils/request.js';

function emptyDataForm() {
    return {
        name: '',
        description: '',
        isEnable: false,
        style: { avatar: '', background: '' },
        srcDbs: [],
        srcSshs: [],
        srcUrls: [],
        srcEs: [],
        sections: [],
        teamIdList: []
    };
}

export default {
    components: { Plus, Refresh, DataAnalysis },
    data() {
        return {
            dialogFormMode: 'create',
            dialogFormVisible: false,
            activeTab: 'base',
            dataForm: emptyDataForm(),
            currentId: '',
            dataViewList: [],
            ownerTeams: [],
            listLoading: false,
            saving: false,
            treeSeq: 0,
            sortables: [],
            tableSortable: null,
            sectionColors: [
                { bg: '#f4f8ff', border: '#cfe0fb' },
                { bg: '#f3fbf6', border: '#cbe9d6' },
                { bg: '#fdfaf0', border: '#f1e3b3' },
                { bg: '#fef7f1', border: '#f6dcc0' },
                { bg: '#fdf5f7', border: '#f5d3db' },
                { bg: '#f7f4fd', border: '#ded1f3' },
                { bg: '#f2fbfc', border: '#c8e8eb' }
            ],
            cardTypeOptions: [
                { value: 'card-chart-bar', label: '横向柱状图', desc: '条形图，柱子水平摆放' },
                { value: 'card-chart-column', label: '纵向柱状图', desc: '柱形图，柱子垂直摆放' },
                { value: 'card-chart-line', label: '折线图', desc: '趋势折线图表' },
                { value: 'card-chart-pie', label: '饼图', desc: '占比饼状图表' },
                { value: 'card-text', label: '文本', desc: '纯文字/指标文本展示卡片' },
                { value: 'card-table', label: '表格', desc: '表格形式展示数据' }
            ]
        };
    },
    mounted() {
        this.loadList();
        this.loadOwnerTeams();
    },
    watch: {
        activeTab(val) {
            if (val === 'sections' && this.dialogFormVisible) {
                this.$nextTick(() => this.initSortables());
            }
        },
        dialogFormVisible(val) {
            if (!val) this.destroySortables();
        }
    },
    computed: {
        sourceCount() {
            const form = this.dataForm;
            return (form.srcDbs || []).length + (form.srcSshs || []).length
                + (form.srcUrls || []).length + (form.srcEs || []).length;
        },
        srcCascaderOptions() {
            const build = list => list.map(x => ({ value: x.id, label: x.name || x.id }));
            const make = (value, label, list) => {
                const children = build(list);
                return { value, label, children, disabled: children.length === 0 };
            };
            return [
                make('db', '数据库', this.dataForm.srcDbs),
                make('ssh', 'SSH', this.dataForm.srcSshs),
                make('url', 'URL', this.dataForm.srcUrls),
                make('es', 'ES', this.dataForm.srcEs)
            ];
        }
    },
    methods: {
        loadList() {
            this.listLoading = true;
            return request({
                url: '/dataview/list',
                method: 'post',
                data: {}
            }).then(res => {
                if (res.code == 0) {
                    this.dataViewList = res.meta.dataViews || [];
                }
            }).finally(() => {
                this.listLoading = false;
                // 列表重绘后需重新绑定拖拽，避免排序失效
                this.$nextTick(() => this.initTableSortable());
            });
        },
        loadOwnerTeams() {
            return request({
                url: '/team/getOwnerTeams',
                method: 'post',
                data: {}
            }).then(res => {
                if (res.code == 0) {
                    this.ownerTeams = res.data || [];
                }
            });
        },
        initTableSortable() {
            this.$nextTick(() => {
                const table = this.$refs.dataViewTable;
                if (!table) return;
                const tbody = table.$el.querySelector('.el-table__body-wrapper tbody');
                if (!tbody) return;
                if (this.tableSortable) {
                    this.tableSortable.destroy();
                    this.tableSortable = null;
                }
                this.tableSortable = Sortable.create(tbody, {
                    animation: 150,
                    handle: '.table-drag-handle',
                    onEnd: (evt) => this.handleTableDragEnd(evt)
                });
            });
        },
        handleTableDragEnd(evt) {
            const { oldIndex, newIndex } = evt;
            if (oldIndex === newIndex) return;
            const moved = this.dataViewList.splice(oldIndex, 1)[0];
            this.dataViewList.splice(newIndex, 0, moved);
            const ids = this.dataViewList.map(x => x.id);
            request({
                url: '/dataview/sort',
                method: 'post',
                data: { ids }
            }).then(() => {
                this.loadList();
            });
        },
        openCreate() {
            this.dialogFormMode = 'create';
            this.currentId = '';
            this.dataForm = emptyDataForm();
            this.activeTab = 'base';
            this.dialogFormVisible = true;
        },
        openEdit(row) {
            this.dialogFormMode = 'edit';
            this.currentId = row.id;
            this.dataForm = this.toEditForm(row);
            this.activeTab = 'base';
            this.dialogFormVisible = true;
        },
        toEditForm(row) {
            return {
                name: row.name || '',
                description: row.description || '',
                isEnable: !!row.isEnable,
                style: row.style || { avatar: '', background: '' },
                srcDbs: (row.srcDbs || []).map(x => ({ ...x })),
                srcSshs: (row.srcSshs || []).map(x => ({ ...x })),
                srcUrls: (row.srcUrls || []).map(x => ({ ...x })),
                srcEs: (row.srcEs || []).map(x => ({ ...x })),
                sections: (row.sections || []).map((s, idx) => ({
                    _id: this.genId(),
                    name: s.name || '',
                    remark: s.remark || '',
                    _colorIndex: idx,
                    cards: (s.cards || []).map(c => ({
                        _id: this.genId(),
                        name: c.name || '',
                        type: c.type || '',
                        srcId: c.srcId || '',
                        srcType: c.srcType || '',
                        _src: c.srcType ? (c.srcId ? [c.srcType, c.srcId] : [c.srcType]) : [],
                        paramsText: c.params ? JSON.stringify(c.params, null, 2) : '',
                        remark: c.remark || ''
                    }))
                })),
                teamIdList: row.teamIdList || []
            };
        },
        toSubmitData() {
            const form = this.dataForm;
            return {
                name: form.name,
                description: form.description,
                isEnable: form.isEnable,
                style: form.style,
                srcDbs: form.srcDbs,
                srcSshs: form.srcSshs,
                srcUrls: form.srcUrls,
                srcEs: form.srcEs,
                sections: form.sections.map(s => ({
                    name: s.name,
                    remark: s.remark,
                    cards: s.cards.map(c => ({
                        name: c.name,
                        type: c.type,
                        srcId: c.srcId,
                        srcType: c.srcType,
                        params: c.paramsText && c.paramsText.trim() ? JSON.parse(c.paramsText) : null,
                        remark: c.remark
                    }))
                })),
                teamIdList: form.teamIdList
            };
        },
        // 返回第一张参数不是合法 JSON 的卡片，便于保存前直接定位到问题卡片
        findInvalidCard() {
            for (const section of this.dataForm.sections) {
                for (const card of section.cards) {
                    const text = card.paramsText && card.paramsText.trim();
                    if (!text) continue;
                    try {
                        JSON.parse(text);
                    } catch (e) {
                        return card;
                    }
                }
            }
            return null;
        },
        save() {
            if (!this.dataForm.name || !this.dataForm.name.trim()) {
                ElMessage({ message: '请输入视图名称', type: 'warning', duration: 2 * 1000 });
                this.activeTab = 'base';
                return;
            }
            const invalidCard = this.findInvalidCard();
            if (invalidCard) {
                ElMessage({
                    message: `卡片「${invalidCard.name || '未命名'}」的参数不是合法的 JSON`,
                    type: 'error',
                    duration: 3 * 1000
                });
                this.activeTab = 'sections';
                return;
            }

            const isCreate = this.dialogFormMode == 'create';
            const data = this.toSubmitData();
            this.saving = true;
            return request({
                url: isCreate ? '/dataview/create' : '/dataview/edit',
                method: 'post',
                data: isCreate ? data : Object.assign({ id: this.currentId }, data)
            }).then(res => {
                if (res.code == 0) {
                    ElMessage({ message: '保存成功', type: 'success', duration: 2 * 1000 });
                    this.dialogFormVisible = false;
                    this.loadList();
                }
            }).finally(() => {
                this.saving = false;
            });
        },
        enableRow(row) {
            const enable = row.isEnable;
            row._enableLoading = true;
            return request({
                url: '/dataview/enable',
                method: 'post',
                data: { id: row.id, enable: enable }
            }).then(res => {
                if (res.code == 0) {
                    ElMessage({
                        message: enable ? '视图已启用' : '视图已停用',
                        type: 'success',
                        duration: 2 * 1000
                    });
                    this.loadList();
                }
            }).catch(() => {
                // 请求失败时回滚开关，避免界面状态与后端不一致
                row.isEnable = !enable;
            }).finally(() => {
                row._enableLoading = false;
            });
        },
        remove(row) {
            return request({
                url: '/dataview/delete',
                method: 'post',
                data: { id: row.id }
            }).then(res => {
                if (res.code == 0) {
                    ElMessage({ message: '视图已删除', type: 'success', duration: 2 * 1000 });
                    this.loadList();
                }
            });
        },
        addSrcDb() {
            this.dataForm.srcDbs.push({
                id: this.nextId(this.dataForm.srcDbs), name: '', url: '', type: '', userName: '',
                password: '', driverClass: '', remark: ''
            });
        },
        addSrcSsh() {
            this.dataForm.srcSshs.push({
                id: this.nextId(this.dataForm.srcSshs), name: '', ip: '', port: '', userName: '',
                password: '', remark: ''
            });
        },
        addSrcUrl() {
            this.dataForm.srcUrls.push({ id: this.nextId(this.dataForm.srcUrls), name: '', url: '', type: 'GET', remark: '' });
        },
        addSrcEs() {
            this.dataForm.srcEs.push({
                id: this.nextId(this.dataForm.srcEs), name: '', serverUrl: '', username: '',
                password: '', remark: ''
            });
        },
        nextId(list) {
            let max = 0;
            list.forEach(item => {
                const n = parseInt(item.id, 10);
                if (!isNaN(n) && n > max) max = n;
            });
            return String(max + 1);
        },
        onCascaderChange(card, value) {
            if (Array.isArray(value) && value.length > 0) {
                card.srcType = value[0];
                card.srcId = value.length > 1 ? value[1] : '';
            } else {
                card.srcType = '';
                card.srcId = '';
            }
        },
        addSection() {
            this.dataForm.sections.push({
                _id: this.genId(), name: '', remark: '',
                _colorIndex: this.dataForm.sections.length,
                cards: []
            });
            this.$nextTick(() => this.initSortables());
        },
        addCard(section) {
            const card = {
                _id: this.genId(), name: '', type: '', srcId: '', srcType: '', _src: [],
                paramsText: '', remark: ''
            };
            section.cards.push(card);
            this.$nextTick(() => this.initSortables());
        },
        removeItem(list, index) {
            list.splice(index, 1);
        },
        removeSection(data) {
            const idx = this.dataForm.sections.findIndex(s => s._id === data._id);
            if (idx >= 0) {
                this.dataForm.sections.splice(idx, 1);
                this.$nextTick(() => this.initSortables());
            }
        },
        removeCard(data) {
            for (const section of this.dataForm.sections) {
                const idx = section.cards.findIndex(c => c._id === data._id);
                if (idx >= 0) {
                    section.cards.splice(idx, 1);
                    this.$nextTick(() => this.initSortables());
                    return;
                }
            }
        },
        genId() {
            this.treeSeq += 1;
            return 'n' + this.treeSeq;
        },
        sectionBoxStyle(section) {
            const idx = (section._colorIndex || 0) % this.sectionColors.length;
            const c = this.sectionColors[idx];
            return { background: c.bg, borderColor: c.border };
        },
        initSortables() {
            this.destroySortables();
            const listEl = this.$refs.sectionList;
            if (!listEl) return;

            this.sortables.push(Sortable.create(listEl, {
                animation: 150,
                handle: '.section-drag-handle',
                draggable: '.section-box',
                onEnd: () => this.syncSectionsFromDom()
            }));

            listEl.querySelectorAll('.card-list').forEach(cardListEl => {
                this.sortables.push(Sortable.create(cardListEl, {
                    animation: 150,
                    handle: '.card-drag-handle',
                    draggable: '.card-box',
                    group: 'dv-cards',
                    onEnd: () => this.syncCardsFromDom()
                }));
            });
        },
        destroySortables() {
            this.sortables.forEach(s => {
                if (s && s.destroy) s.destroy();
            });
            this.sortables = [];
        },
        syncSectionsFromDom() {
            const listEl = this.$refs.sectionList;
            if (!listEl) return;
            const ordered = [];
            Array.from(listEl.children).forEach(el => {
                const id = el.getAttribute('data-section-id');
                const section = this.dataForm.sections.find(s => s._id === id);
                if (section) ordered.push(section);
            });
            this.dataForm.sections = ordered;
        },
        syncCardsFromDom() {
            const listEl = this.$refs.sectionList;
            if (!listEl) return;
            const cardMap = {};
            this.dataForm.sections.forEach(s => s.cards.forEach(c => { cardMap[c._id] = c; }));
            listEl.querySelectorAll('.section-box').forEach(sectionEl => {
                const sectionId = sectionEl.getAttribute('data-section-id');
                const section = this.dataForm.sections.find(s => s._id === sectionId);
                if (!section) return;
                const ordered = [];
                sectionEl.querySelectorAll('.card-list > .card-box').forEach(cardEl => {
                    const cardId = cardEl.getAttribute('data-card-id');
                    const card = cardMap[cardId];
                    if (card) ordered.push(card);
                });
                section.cards = ordered;
            });
        }
    }
};
</script>

<style scoped>
.dataview-page {
    /* 局部覆盖主色，与其它工作台页面保持同一套视觉语言 */
    --el-color-primary: #2f6fed;
    --el-color-primary-light-3: #6d9af2;
    --el-color-primary-light-5: #97b7f6;
    --el-color-primary-light-7: #c1d4fa;
    --el-color-primary-light-8: #d5e2fb;
    --el-color-primary-light-9: #eaf1fd;
    --el-color-primary-dark-2: #2659be;

    --dv-text: #1f2d3d;
    --dv-sub: #5b6a80;
    --dv-muted: #aab4c4;
    --dv-line: #eef1f6;
    --dv-border: #e6eaf0;

    color: var(--dv-text);
}

/* ---------- 工具条 ---------- */
.dv-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    row-gap: 10px;
    padding: 10px 16px;
    border: 1px solid #ebeef5;
    border-radius: 12px;
    background: linear-gradient(180deg, #ffffff 0%, #fafbfd 100%);
    box-shadow: 0 10px 28px -22px rgba(31, 45, 61, 0.55);
}

.dv-toolbar__info {
    display: flex;
    align-items: center;
    min-width: 0;
}

.dv-toolbar__icon {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    margin-right: 10px;
    border-radius: 10px;
    font-size: 18px;
    color: #fff;
    background: linear-gradient(135deg, #6a9cff 0%, #2f6fed 100%);
    box-shadow: 0 8px 16px -10px rgba(47, 111, 237, 0.9);
}

.dv-toolbar__meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.dv-toolbar__title {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: var(--dv-text);
}

.dv-toolbar__subtitle {
    font-size: 12px;
    line-height: 16px;
    color: #a8abb2;
}

.dv-toolbar__actions {
    display: flex;
    flex: none;
    flex-wrap: wrap;
    align-items: center;
    row-gap: 8px;
}

/* ---------- 工具条按钮 ---------- */
.dv-btn {
    height: 34px;
    padding: 0 16px;
    border-radius: 9px;
    font-weight: 500;
    transition: all 0.2s ease;
}

.dv-btn--ghost {
    color: #606266;
    background-color: #fff;
    border-color: #e4e7ed;
}

.dv-btn--ghost:hover,
.dv-btn--ghost:focus {
    color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
    border-color: var(--el-color-primary-light-5);
    transform: translateY(-1px);
    box-shadow: 0 10px 18px -12px rgba(47, 111, 237, 0.9);
}

.dv-btn__icon {
    margin-right: 4px;
}

/* ---------- 列表容器 ---------- */
.dv-panel {
    margin-top: 12px;
    padding: 6px;
    border: 1px solid var(--dv-border);
    border-radius: 14px;
    background-color: #fff;
    box-shadow: 0 1px 2px rgba(28, 47, 84, 0.05);
}

.dv-panel :deep(.el-empty) {
    padding: 40px 0;
}

/* ---------- 表格 ---------- */
.dv-panel :deep(.dv-table) {
    --el-table-header-bg-color: #f7f9fc;
    font-size: 13px;
}

.dv-panel :deep(.dv-table th.el-table__cell) {
    color: var(--dv-muted);
    font-weight: 600;
}

.dv-panel :deep(.dv-table .el-table__cell) {
    padding: 10px 0;
}

.dv-name {
    font-weight: 600;
    color: var(--dv-text);
}

.dv-desc {
    color: var(--dv-sub);
}

.dv-time {
    color: var(--dv-sub);
}

.dv-status {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.dv-switch {
    --el-switch-on-color: #2f6fed;
    --el-switch-off-color: #c8cdd8;
}

.dv-status__text {
    font-size: 12px;
    line-height: 18px;
}

.dv-status__text.is-on {
    color: #2ba471;
}

.dv-status__text.is-off {
    color: #909399;
}

.dv-op {
    margin-right: 14px;
    font-size: 13px;
}

.dv-op:last-child {
    margin-right: 0;
}

/* ---------- 小尺寸设备适配 ---------- */
@media (max-width: 768px) {
    .dv-toolbar {
        padding: 10px 12px;
    }

    .dv-toolbar__subtitle {
        display: none;
    }

    .dv-toolbar__actions {
        flex: 1 1 100%;
    }

    .dv-toolbar__actions .dv-btn {
        flex: 1 1 0;
    }
}
</style>

<!-- 弹窗会 teleport 到 body，样式需放在非 scoped 块并带唯一前缀 -->
<style>
.dv-dialog {
    /* 主色令牌需在此再声明一遍，弹窗取不到页面作用域 */
    --el-color-primary: #2f6fed;
    --el-color-primary-light-3: #6d9af2;
    --el-color-primary-light-5: #97b7f6;
    --el-color-primary-light-7: #c1d4fa;
    --el-color-primary-light-8: #d5e2fb;
    --el-color-primary-light-9: #eaf1fd;
    --el-color-primary-dark-2: #2659be;

    --dv-text: #1f2d3d;
    --dv-sub: #5b6a80;
    --dv-muted: #aab4c4;
    --dv-line: #eef1f6;
    --dv-border: #e6eaf0;

    max-width: calc(100vw - 32px);
    border-radius: 14px;
    box-shadow: 0 18px 50px -12px rgba(28, 47, 84, 0.22);
    color: var(--dv-text);
}

.dv-dialog .el-dialog__header {
    margin-right: 0;
    padding: 20px 24px 0;
}

.dv-dialog .el-dialog__title {
    font-size: 17px;
    font-weight: 600;
    color: var(--dv-text);
}

.dv-dialog .el-dialog__headerbtn {
    top: 14px;
    right: 14px;
}

.dv-dialog .el-dialog__body {
    padding: 14px 24px 4px;
    max-height: 68vh;
    overflow-y: auto;
}

.dv-dialog .el-dialog__footer {
    padding: 0 24px 20px;
}

/* ---------- 弹窗内标签页：改为胶囊样式 ---------- */
.dv-dialog .el-tabs__header {
    margin: 0 0 14px;
}

.dv-dialog .el-tabs__nav-wrap::after {
    height: 0;
}

.dv-dialog .el-tabs__active-bar {
    display: none;
}

.dv-dialog .el-tabs__item {
    height: 34px;
    margin-right: 4px;
    padding: 0 16px;
    border-radius: 9px;
    font-size: 13px;
    font-weight: 500;
    line-height: 34px;
    color: var(--dv-sub);
    transition: background-color 0.2s, color 0.2s;
}

.dv-dialog .el-tabs__item:hover {
    color: var(--dv-text);
    background-color: #f2f5fa;
}

.dv-dialog .el-tabs__item.is-active {
    color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
}

.dv-tab-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.dv-tab-count {
    padding: 0 7px;
    border-radius: 999px;
    background-color: #f2f5fa;
    color: var(--dv-sub);
    font-size: 12px;
    font-style: normal;
    line-height: 18px;
}

.dv-dialog .el-tabs__item.is-active .dv-tab-count {
    background-color: rgba(255, 255, 255, 0.9);
    color: var(--el-color-primary);
}

/* ---------- 弹窗内分区 ---------- */
.dv-dialog .dv-info {
    border: 1px solid var(--dv-border);
    border-radius: 12px;
    margin-bottom: 14px;
    background: #fff;
    overflow: hidden;
}

.dv-dialog .dv-info__title {
    position: relative;
    padding: 12px 16px 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--dv-text);
}

.dv-dialog .dv-info__title::before {
    content: '';
    position: absolute;
    top: 16px;
    left: 8px;
    width: 3px;
    height: 14px;
    border-radius: 2px;
    background: linear-gradient(180deg, #6a9cff, #3f6fff);
}

.dv-dialog .dv-info__title + .dv-info__body {
    padding-top: 12px;
}

.dv-dialog .dv-info__body {
    padding: 14px 16px;
}

.dv-dialog .dv-info__tip {
    margin: 8px 0 0;
    font-size: 12px;
    color: var(--dv-muted);
}

.dv-dialog .el-form-item {
    margin-bottom: 18px;
}

.dv-dialog .el-form-item:last-child {
    margin-bottom: 4px;
}

.dv-dialog .el-form-item__label {
    font-size: 13px;
    font-weight: 500;
    color: #8a94a6;
}

/* ---------- 数据源条目 ---------- */
.dv-dialog .dv-src-item {
    border: 1px solid var(--dv-line);
    border-radius: 10px;
    padding: 12px 14px;
    background-color: #fbfcfe;
}

.dv-dialog .dv-src-item + .dv-src-item {
    margin-top: 10px;
}

.dv-dialog .dv-src-item__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
}

.dv-dialog .dv-src-item__badge {
    padding: 1px 8px;
    border-radius: 999px;
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary);
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
}

.dv-dialog .dv-fields {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px;
}

.dv-dialog .dv-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.dv-dialog .dv-field__label {
    font-size: 12px;
    line-height: 16px;
    color: #8a94a6;
}

/* ---------- 空状态与新增按钮 ---------- */
.dv-dialog .dv-empty-tip {
    margin: 10px 0 0;
    font-size: 12px;
    color: var(--dv-muted);
}

.dv-dialog .dv-empty-tip--center {
    text-align: center;
}

.dv-dialog .dv-add-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 32px;
    margin-top: 10px;
    padding: 0 14px;
    border: 1px dashed #cfd8e6;
    border-radius: 9px;
    background-color: #fff;
    color: var(--el-color-primary);
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.dv-dialog .dv-add-btn:hover {
    border-color: var(--el-color-primary-light-5);
    background-color: var(--el-color-primary-light-9);
}

.dv-dialog .dv-add-btn--block {
    width: 100%;
}

.dv-dialog .dv-add-btn--lg {
    height: 40px;
    margin-top: 0;
}

/* ---------- 区块 / 卡片 ---------- */
.dv-dialog .section-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 60px;
}

.dv-dialog .section-box {
    border: 1px solid #d9e3f3;
    border-radius: 12px;
    padding: 14px;
}

.dv-dialog .section-box-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}

.dv-dialog .section-name-input {
    flex: 1;
}

.dv-dialog .dv-count-pill {
    flex: none;
    padding: 1px 8px;
    border-radius: 999px;
    background-color: rgba(255, 255, 255, 0.85);
    color: var(--dv-sub);
    font-size: 12px;
    line-height: 18px;
    white-space: nowrap;
}

.dv-dialog .card-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 30px;
}

.dv-dialog .card-box {
    background-color: #ffffff;
    border: 1px solid var(--dv-border);
    border-radius: 10px;
    padding: 12px;
    box-shadow: 0 1px 2px rgba(28, 47, 84, 0.04);
}

.dv-dialog .card-box-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
}

.dv-dialog .card-name-input {
    flex: 1;
    min-width: 0;
}

.dv-dialog .card-type-select {
    width: 150px;
    flex-shrink: 0;
}

.dv-dialog .card-src-select {
    width: 190px;
    flex-shrink: 0;
}

.dv-dialog .card-params-input textarea {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    line-height: 18px;
}

.dv-dialog .add-card-btn-wrap {
    margin-top: 12px;
}

.dv-dialog .add-card-btn-wrap .dv-add-btn {
    margin-top: 0;
}

.dv-dialog .add-section-btn-wrap {
    margin-top: 12px;
}

/* ---------- 拖拽手柄 ---------- */
.drag-handle {
    display: inline-flex;
    flex-direction: column;
    gap: 2px;
    padding: 2px 4px;
    cursor: move;
    user-select: none;
}

.drag-handle i {
    width: 14px;
    height: 2px;
    background: #9aa7bd;
    border-radius: 1px;
}

.section-box.sortable-ghost,
.card-box.sortable-ghost {
    opacity: 0.4;
}

.section-box.sortable-drag,
.card-box.sortable-drag {
    box-shadow: 0 4px 12px rgba(47, 111, 237, 0.3);
}

/* ---------- 小尺寸设备适配 ---------- */
@media (max-width: 768px) {
    .dv-dialog .el-dialog__body {
        padding: 12px 14px 4px;
    }

    .dv-dialog .el-dialog__header {
        padding: 16px 16px 0;
    }

    .dv-dialog .el-form-item__label {
        width: 84px !important;
    }

    .dv-dialog .card-box-header {
        flex-wrap: wrap;
        row-gap: 8px;
    }

    .dv-dialog .card-name-input {
        flex: 1 1 calc(100% - 120px);
    }

    .dv-dialog .card-type-select,
    .dv-dialog .card-src-select {
        flex: 1 1 calc(50% - 4px);
        width: auto;
    }

    .dv-dialog .section-box-header {
        flex-wrap: wrap;
        row-gap: 8px;
    }

    .dv-dialog .section-name-input {
        flex: 1 1 100%;
    }
}
</style>
